import { motion } from "framer-motion";
import { Leaf, Ruler, ShieldCheck, Sun, Layers, Wind, Home, Building2, Thermometer, Activity, Sparkles } from "lucide-react";
import { MaskLines, Reveal, SectionLabel } from "./Motion";
import { useT } from "@/i18n";

const GROUPS = [
  { id: "sus", icon: Leaf, title: "Sostenibilità e bioedilizia", items: [
    [Sun, "Zero combustibili fossili", "Strutture realizzate al 100% senza l'uso di gas o gasolio, basate esclusivamente su impianti a energia rinnovabile: fotovoltaico, biomassa, geotermia."],
    [Layers, "Isolamento naturale personalizzabile", "Pareti, solai e tetti isolati con materiali ecologici certificati come fibra di legno, sughero, fibra di canapa e lana di pecora."],
    [Wind, "Risparmio di CO₂ certificato", "Riduzione drastica delle emissioni di anidride carbonica sia in fase di costruzione, rispetto all'edilizia tradizionale, sia nella gestione quotidiana: pari a zero emissioni."],
  ] },
  { id: "ver", icon: Ruler, title: "Versatilità e ambiti costruttivi", items: [
    [Home, "Residenziale su misura", "Ville private e condomini personalizzabili per stile (classico, contemporaneo, minimal) e budget, dalle micro alle grandi metrature."],
    [Building2, "Settore professionale e pubblico", "Strutture prefabbricate avanzate per hotel, edilizia scolastica, centri sanitari, uffici, spazi industriali, ampliamenti e sopraelevazioni."],
    [Thermometer, "Costruzioni estreme (off-grid)", "Tecnologia testata per garantire indipendenza energetica e comfort termico costante anche in climi torridi, fino a 55 °C, con monitoraggio tramite sensori interni."],
  ] },
  { id: "sec", icon: ShieldCheck, title: "Sicurezza antisismica e \"antidanno\"", items: [
    [Activity, "Resistenza collaudata", "Edifici collaudati capaci di superare sollecitazioni sismiche estreme, superiori ai più devastanti terremoti storici."],
    [Sparkles, "Certificazione antidanno", "La flessibilità del legno strutturale protegge l'edificio non solo dal crollo, ma evita crepe e lesioni a intonaci, finiture interne e infissi."],
  ] },
];

const Item = ({ it, k, t }) => {
  const [Icon, title, text] = it;
  return (
    <li className="flex gap-4 border-t border-white/15 pt-5" data-testid={`bio-item-${k}`}>
      <Icon className="mt-1 h-5 w-5 shrink-0 text-[#B9D58F]" />
      <div>
        <h4 className="font-display text-lg font-bold leading-snug">{t(`bio.${k}.t`, title)}</h4>
        <p className="mt-2 text-sm leading-relaxed text-[#E1E9D6] sm:text-base">{t(`bio.${k}.d`, text)}</p>
      </div>
    </li>
  );
};

const Group = ({ g, i, t }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] }}
    data-testid={`bio-group-${g.id}`}
  >
    <div className="flex items-center gap-3">
      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#B9D58F] text-[#1F3314]"><g.icon className="h-5 w-5" /></span>
      <h3 className="font-display text-2xl font-bold tracking-tight">{t(`bio.${g.id}`, g.title)}</h3>
    </div>
    <ul className="mt-6 space-y-6">
      {g.items.map((it, j) => <Item key={it[1]} it={it} k={`${g.id}${j}`} t={t} />)}
    </ul>
  </motion.div>
);

export const BioHouse = () => {
  const t = useT();
  return (
    <section id="bioedilizia" className="keep-dark relative bg-[#1F3314] px-5 py-28 text-[#F1F5EA] sm:px-8 lg:py-40" data-testid="bio-section">
      <div className="mx-auto max-w-[1500px]">
        <SectionLabel index="04">{t("bio.label", "Case in bioedilizia")}</SectionLabel>
        <div className="mt-10 grid gap-12 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <MaskLines
              className="font-display text-4xl font-bold leading-[1.02] tracking-tight sm:text-5xl lg:text-[4.2rem]"
              lines={[t("bio.h1", "La casa in legno fatta"), <span key="b">{t("bio.h2a", "come il tuo")} <em className="font-serif font-normal text-[#B9D58F]">{t("bio.h2em", "io migliore.")}</em></span>]}
            />
            <Reveal delay={0.2} className="mt-8 max-w-2xl space-y-4 text-base leading-relaxed text-[#E1E9D6] sm:text-lg">
              <p>{t("bio.intro", "Massimo comfort abitativo, altissima efficienza energetica, benessere e sicurezza sismica garantita.")}</p>
              <p data-testid="bio-intro-seo">{t("bio.intro2", "Progettiamo e costruiamo case in legno prefabbricate chiavi in mano, sopraelevazioni e ampliamenti volumetrici in legno, edilizia scolastica e sanitaria: architettura sostenibile con materiali naturali certificati, antisismica, resistente al fuoco e a energia positiva.")}</p>
            </Reveal>
          </div>
          <Reveal delay={0.25} className="bg-[#B9D58F] p-8 text-[#1F3314] lg:col-span-5" data-testid="bio-greendeal">
            <p className="font-mono text-xs uppercase tracking-[0.2em]">{t("bio.gdLabel", "Oltre il Green Deal europeo")}</p>
            <p className="mt-4 font-display text-xl font-bold leading-snug sm:text-2xl">{t("bio.gdText", "Abitazioni progettate per produrre più energia di quanta ne consumino, già in linea con la neutralità climatica e destinate a valorizzarsi nel tempo.")}</p>
          </Reveal>
        </div>
        <div className="mt-20 grid gap-16 lg:grid-cols-3 lg:gap-12">
          {GROUPS.map((g, i) => <Group key={g.id} g={g} i={i} t={t} />)}
        </div>
      </div>
    </section>
  );
};
